import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import PushNotificationsToggle from "../components/PushNotificationsToggle";
import IdentityVerificationCard from "../components/IdentityVerificationCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ShieldCheck,
  Smartphone,
  Monitor,
  Tablet,
  Loader2,
  ArrowLeft,
  LogIn,
  LogOut,
  ShieldOff,
  CheckCircle2,
  XCircle,
  Trash2,
  Mail,
  Phone,
} from "lucide-react";
import { toast } from "sonner";

function DeviceIcon({ device, className = "w-5 h-5" }) {
  if (device === "mobile") return <Smartphone className={className} />;
  if (device === "tablet") return <Tablet className={className} />;
  return <Monitor className={className} />;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Justo ahora";
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  return `Hace ${Math.floor(diff / 86400)} d`;
}

const EVENT_LABELS = {
  login_success: { label: "Inicio de sesión exitoso", Icon: LogIn, tone: "emerald" },
  login_fail: { label: "Intento de inicio fallido", Icon: XCircle, tone: "red" },
  otp_verify_success: { label: "Código 2FA verificado", Icon: CheckCircle2, tone: "emerald" },
  otp_verify_fail: { label: "Código 2FA incorrecto", Icon: XCircle, tone: "red" },
  logout: { label: "Cierre de sesión", Icon: LogOut, tone: "neutral" },
  "2fa_toggled": { label: "Configuración 2FA modificada", Icon: ShieldCheck, tone: "amber" },
  "2fa_method_changed": { label: "Método 2FA cambiado", Icon: ShieldCheck, tone: "amber" },
  device_revoked: { label: "Dispositivo revocado", Icon: Trash2, tone: "amber" },
  phone_verified: { label: "Teléfono verificado", Icon: Phone, tone: "emerald" },
  phone_removed: { label: "Teléfono eliminado", Icon: Phone, tone: "amber" },
};

export default function SecurityPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  // Disable 2FA dialog
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disabling, setDisabling] = useState(false);

  // Revoke confirm dialogs
  const [revokeTarget, setRevokeTarget] = useState(null); // device object or "all"
  const [revoking, setRevoking] = useState(false);

  // Phone verification
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | code | savingMethod
  const [phoneCode, setPhoneCode] = useState("");
  const [sendingPhone, setSendingPhone] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [removingPhone, setRemovingPhone] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/seguridad" }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/auth/security/overview");
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la información de seguridad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) loadOverview();
  }, [isAuthenticated]);

  const handleToggle2FA = async (next) => {
    if (!next) {
      // Disabling — require password confirmation
      setDisableOpen(true);
      return;
    }
    setSavingToggle(true);
    try {
      await api.put("/auth/security/2fa", { enabled: true });
      toast.success("2FA activado");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSavingToggle(false);
    }
  };

  const handleConfirmDisable = async () => {
    if (!disablePassword) {
      toast.error("Ingresa tu contraseña");
      return;
    }
    setDisabling(true);
    try {
      await api.put("/auth/security/2fa", { enabled: false, password: disablePassword });
      toast.success("2FA desactivado. Recomendamos volver a activarlo lo antes posible.");
      setDisableOpen(false);
      setDisablePassword("");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desactivar");
    } finally {
      setDisabling(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      if (revokeTarget === "all") {
        await api.delete("/auth/security/devices");
        toast.success("Todos los dispositivos fueron revocados");
      } else {
        await api.delete(`/auth/security/devices/${revokeTarget.device_id}`);
        toast.success("Dispositivo revocado");
      }
      setRevokeTarget(null);
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo revocar");
    } finally {
      setRevoking(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!phoneInput.trim()) {
      toast.error("Ingresa tu número de teléfono");
      return;
    }
    setSendingPhone(true);
    try {
      const { data } = await api.post("/auth/security/phone/start", { phone: phoneInput.trim() });
      if (data?.already_verified) {
        toast.success("Este número ya está verificado");
        await loadOverview();
      } else {
        toast.success(`Te enviamos un código a ${data.phone_masked}`);
        setPhoneStep("code");
        setPhoneCode("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el código");
    } finally {
      setSendingPhone(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    const code = phoneCode.trim();
    if (code.length < 4) {
      toast.error("Ingresa el código de verificación");
      return;
    }
    setVerifyingPhone(true);
    try {
      await api.post("/auth/security/phone/verify", { code });
      toast.success("Número verificado correctamente");
      setPhoneStep("idle");
      setPhoneInput("");
      setPhoneCode("");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorrecto o expirado");
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleRemovePhone = async () => {
    if (!window.confirm("¿Eliminar el número verificado? Tu método 2FA volverá a Email.")) return;
    setRemovingPhone(true);
    try {
      await api.delete("/auth/security/phone");
      toast.success("Número eliminado");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setRemovingPhone(false);
    }
  };

  const handleChangeMethod = async (next) => {
    if (!data || data.two_fa_method === next) return;
    setSavingMethod(true);
    try {
      await api.put("/auth/security/2fa/method", { method: next });
      toast.success(next === "sms" ? "Recibirás códigos por SMS" : "Recibirás códigos por email");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar el método");
    } finally {
      setSavingMethod(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" data-testid="security-loading">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!data) return null;

  const { two_fa_enabled, two_fa_method = "email", email_masked, phone_masked, phone_verified, devices = [], activity = [] } = data;

  return (
    <div className="min-h-screen bg-sand" data-testid="security-page">
      <div className="container-main py-6 md:py-10 max-w-3xl">
        <Link
          to="/profile"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="security-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al perfil
        </Link>

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-2">
          Seguridad
        </h1>
        <p className="text-neutral-600 mb-8">
          Gestiona la verificación en dos pasos, los dispositivos de confianza y revisa la
          actividad de tu cuenta.
        </p>

        {/* 2FA Toggle */}
        <div className="card-base mb-6" data-testid="security-2fa-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div
                className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  two_fa_enabled
                    ? "bg-emerald-500/10 text-emerald-600"
                    : "bg-amber-500/10 text-amber-600"
                }`}
              >
                {two_fa_enabled ? <ShieldCheck className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-lg text-agave">
                  Verificación en dos pasos
                </h3>
                <p className="text-sm text-neutral-600">
                  Te enviamos un código a{" "}
                  <span className="font-medium text-agave">{email_masked || "tu correo"}</span>{" "}
                  cada vez que inicias sesión desde un nuevo dispositivo.
                </p>
                {!two_fa_enabled && (
                  <p className="text-xs text-amber-700 mt-2 bg-amber-50 px-3 py-2 rounded-lg inline-block">
                    Tu cuenta está más expuesta sin 2FA. Recomendamos activarlo.
                  </p>
                )}
              </div>
            </div>
            <Switch
              checked={two_fa_enabled}
              disabled={savingToggle}
              onCheckedChange={handleToggle2FA}
              data-testid="security-2fa-toggle"
              className="data-[state=checked]:bg-emerald-600"
            />
          </div>
        </div>

        {/* 2FA Method Picker (only when 2FA is enabled) */}
        {two_fa_enabled && (
          <div className="card-base mb-6" data-testid="security-method-card">
            <h3 className="font-heading font-semibold text-lg text-agave mb-1">
              Cómo recibir tu código
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              Elige cómo te enviamos el código de 6 dígitos al iniciar sesión.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleChangeMethod("email")}
                disabled={savingMethod}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  two_fa_method === "email"
                    ? "border-emerald-600 bg-emerald-50"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
                data-testid="security-method-email"
              >
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-agave">Email</p>
                    <p className="text-xs text-neutral-600 truncate">{email_masked}</p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleChangeMethod("sms")}
                disabled={savingMethod || !phone_verified}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  two_fa_method === "sms"
                    ? "border-emerald-600 bg-emerald-50"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${!phone_verified ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid="security-method-sms"
              >
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-agave">SMS</p>
                    <p className="text-xs text-neutral-600 truncate">
                      {phone_verified ? phone_masked : "Verifica un número abajo"}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Phone Verification */}
        <div className="card-base mb-6" data-testid="security-phone-card">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div
                className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  phone_verified ? "bg-emerald-500/10 text-emerald-600" : "bg-terracotta/10 text-terracotta"
                }`}
              >
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-agave">
                  Número de teléfono
                </h3>
                <p className="text-sm text-neutral-600">
                  {phone_verified
                    ? `Verificado: ${phone_masked}`
                    : "Verifica tu número para recibir códigos por SMS."}
                </p>
              </div>
            </div>
            {phone_verified && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemovePhone}
                disabled={removingPhone}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="security-phone-remove-btn"
              >
                {removingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar"}
              </Button>
            )}
          </div>

          {!phone_verified && phoneStep === "idle" && (
            <div className="flex gap-2 flex-wrap" data-testid="security-phone-form">
              <Input
                type="tel"
                placeholder="+52 664 311 2233"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="flex-1 min-w-[200px]"
                data-testid="security-phone-input"
              />
              <Button
                onClick={handleSendPhoneCode}
                disabled={sendingPhone}
                className="bg-[#111111] hover:bg-neutral-800 text-white"
                data-testid="security-phone-send-btn"
              >
                {sendingPhone ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enviar código
              </Button>
            </div>
          )}

          {!phone_verified && phoneStep === "code" && (
            <div className="space-y-3" data-testid="security-phone-code-form">
              <p className="text-sm text-neutral-700">
                Ingresa el código de 6 dígitos que te enviamos por SMS.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="flex-1 min-w-[140px] text-center text-lg font-mono tracking-[0.3em]"
                  data-testid="security-phone-code-input"
                  autoFocus
                />
                <Button
                  onClick={handleVerifyPhoneCode}
                  disabled={verifyingPhone || phoneCode.length !== 6}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="security-phone-verify-btn"
                >
                  {verifyingPhone ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Verificar
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPhoneStep("idle");
                    setPhoneCode("");
                  }}
                  data-testid="security-phone-cancel-btn"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Identity verification (Stripe Identity) */}
        <IdentityVerificationCard />

        {/* Push notifications (Web Push / PWA) */}
        <PushNotificationsToggle />

        {/* Trusted Devices */}
        <div className="card-base mb-6" data-testid="security-devices-card">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <h3 className="font-heading font-semibold text-lg text-agave">
                Dispositivos de confianza
              </h3>
              <p className="text-sm text-neutral-600 mt-1">
                Estos dispositivos pueden iniciar sesión sin pedir código por 30 días.
              </p>
            </div>
            {devices.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRevokeTarget("all")}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="security-revoke-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Revocar todos
              </Button>
            )}
          </div>

          {devices.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">
              No tienes dispositivos de confianza activos. Marca "Confiar en este dispositivo" al
              iniciar sesión para agregar uno.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="security-devices-list">
              {devices.map((d) => (
                <li
                  key={d.device_id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-neutral-200 bg-white"
                  data-testid={`device-row-${d.device_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <DeviceIcon device={d.device} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-agave capitalize truncate">
                          {d.browser || "Navegador"} · {d.os || "OS"}
                        </p>
                        {d.is_current && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">
                            Este dispositivo
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 truncate">
                        {[d.city, d.country].filter(Boolean).join(", ") || d.ip || "Ubicación desconocida"}{" "}
                        · Último uso {timeAgo(d.last_used_at)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRevokeTarget(d)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                    data-testid={`device-revoke-${d.device_id}`}
                  >
                    Revocar
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Activity log */}
        <div className="card-base" data-testid="security-activity-card">
          <h3 className="font-heading font-semibold text-lg text-agave mb-1">
            Actividad reciente
          </h3>
          <p className="text-sm text-neutral-600 mb-4">
            Últimos 20 eventos relacionados con tu cuenta.
          </p>

          {activity.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">
              Aún no hay eventos para mostrar.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-200" data-testid="security-activity-list">
              {activity.map((e) => {
                const cfg = EVENT_LABELS[e.kind] || { label: e.kind, Icon: ShieldCheck, tone: "neutral" };
                const Icon = cfg.Icon;
                const toneClass = {
                  emerald: "bg-emerald-100 text-emerald-700",
                  red: "bg-red-100 text-red-700",
                  amber: "bg-amber-100 text-amber-700",
                  neutral: "bg-neutral-100 text-neutral-700",
                }[cfg.tone];
                return (
                  <li
                    key={e.event_id}
                    className="py-3 flex items-start gap-3"
                    data-testid={`activity-row-${e.event_id}`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClass}`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-agave">{cfg.label}</p>
                      <p className="text-xs text-neutral-500">
                        {[e.city, e.country].filter(Boolean).join(", ") || e.ip || "Ubicación desconocida"}
                        {" · "}
                        {[e.browser, e.os].filter(Boolean).join(" · ")}
                        {" · "}
                        {timeAgo(e.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Disable 2FA dialog */}
      <Dialog open={disableOpen} onOpenChange={(o) => !disabling && setDisableOpen(o)}>
        <DialogContent data-testid="disable-2fa-dialog">
          <DialogHeader>
            <DialogTitle>Desactivar verificación en dos pasos</DialogTitle>
            <DialogDescription>
              Tu cuenta quedará protegida solo por tu contraseña. Confirma tu contraseña para
              continuar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="disable-password">Contraseña actual</Label>
            <Input
              id="disable-password"
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="••••••••"
              data-testid="disable-2fa-password-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDisableOpen(false);
                setDisablePassword("");
              }}
              disabled={disabling}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDisable}
              disabled={disabling || !disablePassword}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="disable-2fa-confirm-btn"
            >
              {disabling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Desactivar 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => !revoking && !o && setRevokeTarget(null)}>
        <DialogContent data-testid="revoke-device-dialog">
          <DialogHeader>
            <DialogTitle>
              {revokeTarget === "all"
                ? "Revocar todos los dispositivos"
                : "Revocar este dispositivo"}
            </DialogTitle>
            <DialogDescription>
              {revokeTarget === "all"
                ? "Todos los dispositivos pedirán verificación en dos pasos la próxima vez que inicien sesión, incluido este. Tu sesión actual se mantiene."
                : "Este dispositivo pedirá verificación en dos pasos la próxima vez que inicie sesión."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={revoking}>
              Cancelar
            </Button>
            <Button
              onClick={handleRevokeConfirm}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="revoke-confirm-btn"
            >
              {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revocar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
